import { createApi } from '@reduxjs/toolkit/query/react'

import {
  BotWorkflowGroup,
  BotWorkflowNode,
} from '@/model/bot'
import { botVersionSlice } from '@/redux/bot-version'
import { baseQuery } from '@/redux/utils'

export interface CreateBotWorkflowGroupRequest extends Omit<BotWorkflowGroup, 'id' | 'nodes'> {
  botId: number
  botVersionId: number
}

export interface UpdateBotWorkflowGroupRequest extends Partial<Omit<BotWorkflowGroup, 'id' | 'nodes'>> {
  id: number
  botId: number
  botVersionId: number
}

export interface RemoveBotWorkflowGroupRequest {
  id: number
  botId: number
  botVersionId: number
}

export interface CreateBotWorkflowNodeRequest extends Omit<BotWorkflowNode, 'id'> {
  botId: number
  botVersionId: number
  botWorkflowGroupId: number
}

export interface UpdateBotWorkflowNodeRequest extends Partial<Omit<BotWorkflowNode, 'id'>> {
  id: number
  botId: number
  botVersionId: number
  botWorkflowGroupId: number
}

export interface RemoveBotWorkflowNodeRequest {
  id: number
  botId: number
  botVersionId: number
  botWorkflowGroupId: number
}

export const botWorkflowSlice = createApi({
  reducerPath: 'botWorkflow',
  baseQuery,
  endpoints: builder => ({
    createBotWorkflowGroup: builder.mutation<BotWorkflowGroup, CreateBotWorkflowGroupRequest>({
      query: ({ botVersionId, ...workflowGroup }) => ({
        url: `/bot-version/${botVersionId}/bot-workflow-group`,
        method: 'POST',
        body: workflowGroup,
      }),
      async onQueryStarted({ botId, botVersionId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          draft
            .find(({ id }) => id === botVersionId)
            ?.workflowGroups?.push(data)
        }))
      },
    }),
    updateBotWorkflowGroup: builder.mutation<BotWorkflowGroup, UpdateBotWorkflowGroupRequest>({
      query: (botWorkflowGroup) => ({
        url: `/bot-workflow-group/${botWorkflowGroup.id}`,
        method: 'PUT',
        body: botWorkflowGroup,
      }),
      async onQueryStarted(botWorkflowGroup, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botWorkflowGroup.botId, (draft) => {
          const workflowGroup = draft
            .find(({ id }) => id === botWorkflowGroup.botVersionId)
            ?.workflowGroups?.find(({ id }) => id === botWorkflowGroup.id)
          if (workflowGroup) {
            Object.assign(workflowGroup, data)
          }
        }))
      },
    }),
    removeBotWorkflowGroup: builder.mutation<void, RemoveBotWorkflowGroupRequest>({
      query: ({ id }) => ({
        url: `/bot-workflow-group/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ botId, botVersionId, id: workflowGroupId }, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          const workflowGroups = draft
            .find(({ id }) => id === botVersionId)
            ?.workflowGroups
          if (workflowGroups) {
            const workflowGroupIndex = workflowGroups.findIndex(({ id }) => id === workflowGroupId)
            if (workflowGroupIndex !== undefined && workflowGroupIndex >= 0) {
              workflowGroups.splice(workflowGroupIndex, 1)
            }
          }
        }))
      },
    }),
    createBotWorkflowNode: builder.mutation<BotWorkflowNode, CreateBotWorkflowNodeRequest>({
      query: ({ botWorkflowGroupId, ...node }) => ({
        url: `/bot-workflow-group/${botWorkflowGroupId}/node`,
        method: 'POST',
        body: node,
      }),
      async onQueryStarted({ botId, botVersionId, botWorkflowGroupId }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          draft
            .find(({ id }) => id === botVersionId)
            ?.workflowGroups?.find(({ id }) => id === botWorkflowGroupId)
            ?.nodes?.push(data)
        }))
      },
    }),
    updateBotWorkflowNode: builder.mutation<BotWorkflowNode, UpdateBotWorkflowNodeRequest>({
      query: (botWorkflowNode) => ({
        url: `/bot-workflow-node/${botWorkflowNode.id}`,
        method: 'PUT',
        body: botWorkflowNode,
      }),
      async onQueryStarted(botWorkflowNode, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botWorkflowNode.botId, (draft) => {
          const workflowGroup = draft
            .find(({ id }) => id === botWorkflowNode.botVersionId)
            ?.workflowGroups?.find(({ id }) => id === botWorkflowNode.botWorkflowGroupId)
          if (workflowGroup) {
            const node = workflowGroup.nodes?.find(({ id }) => id === botWorkflowNode.id)
            if (node) {
              Object.assign(node, data)
            }
          }
        }))
      },
    }),
    removeBotWorkflowNode: builder.mutation<void, RemoveBotWorkflowNodeRequest>({
      query: ({ id }) => ({
        url: `/bot-workflow-node/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { botId, botVersionId, id: workflowNodeId, botWorkflowGroupId },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled
        dispatch(botVersionSlice.util.updateQueryData('readAllBotVersions', botId, (draft) => {
          const workflowNodes = draft
            .find(({ id }) => id === botVersionId)
            ?.workflowGroups
            ?.find(({ id }) => id === botWorkflowGroupId)
            ?.nodes
          if (workflowNodes) {
            const workflowNodeIndex = workflowNodes.findIndex(({ id }) => id === workflowNodeId)
            if (workflowNodeIndex !== undefined && workflowNodeIndex >= 0) {
              workflowNodes.splice(workflowNodeIndex, 1)
            }
          }
        }))
      },
    }),
  }),
})

export const {
  useCreateBotWorkflowGroupMutation,
  useUpdateBotWorkflowGroupMutation,
  useRemoveBotWorkflowGroupMutation,
  useCreateBotWorkflowNodeMutation,
  useUpdateBotWorkflowNodeMutation,
  useRemoveBotWorkflowNodeMutation,
} = botWorkflowSlice
